import { orgRole, projectRole, type OrgRole, type PendingUser, type ProjectRole } from '@p2b/contract';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { ErrorNote } from '../App.js';
import { api } from '../api.js';
import { formatDate, label, useI18n } from '../i18n.js';

/** Superadmins: people who signed in and have nothing yet, and a way to place them. */
export function AdminUsers() {
  const { t, lang } = useI18n();
  const users = useQuery({ queryKey: ['pending-users'], queryFn: () => api.pendingUsers() });
  return (
    <div className="page">
      <h1 className="page-title">{t('pendingUsers')}</h1>
      <ErrorNote error={users.error} />
      {users.data?.length === 0 && <p className="empty">{t('noPendingUsers')}</p>}
      <div className="stack">
        {(users.data ?? []).map((u) => (
          <div key={u.id} className="panel stack">
            <div className="row spread">
              <div>
                <div className="cell-strong">{u.name || u.email}</div>
                <div className="muted">
                  {u.name ? u.email : null}{' '}
                  {u.emailVerified && <span className="tag ok">{t('verified')}</span>}
                </div>
              </div>
              <span className="muted">{formatDate(u.createdAt, lang)}</span>
            </div>
            <AssignForm user={u} />
          </div>
        ))}
      </div>
    </div>
  );
}

function AssignForm({ user }: { user: PendingUser }) {
  const { t } = useI18n();
  const qc = useQueryClient();
  const orgs = useQuery({ queryKey: ['orgs'], queryFn: () => api.orgs() });
  const projects = useQuery({ queryKey: ['projects'], queryFn: () => api.projects() });
  const [form, setForm] = useState({
    org: '',
    orgRole: 'member' as OrgRole,
    project: '',
    projectRole: 'client' as ProjectRole,
  });
  const assign = useMutation({
    mutationFn: () =>
      api.assignUser(user.id, {
        ...(form.org ? { org: form.org, orgRole: form.orgRole } : {}),
        ...(form.project ? { project: form.project, projectRole: form.projectRole } : {}),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['pending-users'] });
      void qc.invalidateQueries({ queryKey: ['me'] });
    },
  });
  const submit = (e: FormEvent) => {
    e.preventDefault();
    assign.mutate();
  };
  const role = (r: string) => (
    <option key={r} value={r}>
      {label(t, 'role', r)}
    </option>
  );
  return (
    <form className="assign" onSubmit={submit}>
      <div className="form-grid">
        <label>
          {t('organization')}
          <select value={form.org} onChange={(e) => setForm({ ...form, org: e.target.value })}>
            <option value="">{t('none')}</option>
            {(orgs.data ?? []).map((o) => (
              <option key={o.id} value={o.slug}>
                {o.name}
              </option>
            ))}
          </select>
        </label>
        <label>
          {t('role')}
          <select
            value={form.orgRole}
            onChange={(e) => setForm({ ...form, orgRole: e.target.value as OrgRole })}
          >
            {orgRole.options.map(role)}
          </select>
        </label>
        <label>
          {t('projects')}
          <select value={form.project} onChange={(e) => setForm({ ...form, project: e.target.value })}>
            <option value="">{t('none')}</option>
            {(projects.data ?? []).map((p) => (
              <option key={p.id} value={p.id}>
                {p.orgName}: {p.name}
              </option>
            ))}
          </select>
        </label>
        <label>
          {t('projectRole')}
          <select
            value={form.projectRole}
            onChange={(e) => setForm({ ...form, projectRole: e.target.value as ProjectRole })}
          >
            {projectRole.options.map(role)}
          </select>
        </label>
      </div>
      <ErrorNote error={assign.error} />
      <div>
        <button className="primary" disabled={(!form.org && !form.project) || assign.isPending}>
          {t('assign')}
        </button>
      </div>
    </form>
  );
}
